import { AccreditationPortalLayout } from "@/components/layouts/AccreditationPortalLayout";
import { Loading } from "@/components/portal/Loading";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { dateToPortalDisplay, formatDateWithoutOrdinal } from "@/utils/date";
import { parseISO } from "date-fns";
import {
  DownloadIcon,
  QrCode,
  SquareArrowOutUpRight,
  TicketIcon,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import axios from "axios";
import { IndividualTicketDownloadResponse } from "@/models/accreditation";
import { Modal } from "@/components/dashboard/Modal";
import { useState } from "react";
import QRCode from "react-qr-code";
import { BASE_URL } from "@/dashboardQueries";
import { useDarkMode } from "@/hooks";

export const Tickets = () => {
  const { individualId } = useParams<{ individualId: string }>();
  const navigate = useNavigate();
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] =
    useState<IndividualTicketDownloadResponse | null>(null);
  const isDarkMode = useDarkMode();

  const { data, isLoading, isError } = useQuery(
    ["tickets", individualId],
    async (): Promise<IndividualTicketDownloadResponse> => {
      if (!individualId) {
        throw new Error("Missing individualId");
      }
      const response = await axios.get(`${BASE_URL}/tickets/${individualId}`);
      return response.data;
    },
    {
      onError: (error: any) => {
        if (error.response?.status === 404 || !individualId) {
          navigate("https://ticketr.events"); // Redirect to a URL if 404 or individualId is missing
        }
      },
      enabled: !!individualId, // Only fetch if individualId exists
    }
  );

  const downloadTicket = useMutation(
    async (ticketUuid: string) => {
      const response = await axios.get(
        `${BASE_URL}/tickets/${individualId}/download/${ticketUuid}`,
        {
          responseType: "blob", // Ensure the response is treated as a file
        }
      );
      return response;
    },
    {
      onSuccess: (response, ticketUuid) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${ticketUuid}.pdf`); // Set the filename
        document.body.appendChild(link);
        link.click();
        link.remove();
      },
      onError: () => {
        alert("Failed to download the ticket. Please try again.");
      },
    }
  );

  if (isLoading) {
    return (
      <AccreditationPortalLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loading colour="white" />
        </div>
      </AccreditationPortalLayout>
    );
  }

  if (isError) {
    return (
      <AccreditationPortalLayout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-red-500">Failed to load tickets.</p>
        </div>
      </AccreditationPortalLayout>
    );
  }

  const closeQRCodeModal = () => {
    setIsQRCodeModalOpen(false);
    setTimeout(() => setSelectedTicket(null), 500);
  };

  return (
    <AccreditationPortalLayout>
      <Modal isOpen={isQRCodeModalOpen} setIsOpen={closeQRCodeModal}>
        <div className="p-3 rounded-md bg-zinc-800">
          <div className="flex items-center justify-center">
            <QRCode
              className="w-32 h-32"
              value={selectedTicket?.uuid || ""}
              bgColor={"transparent"}
              fgColor={"white"}
            />
          </div>
          <div className="mt-3 text-center">
            <h1 className="text-lg font-semibold text-gray-200">
              {selectedTicket?.ticket}
            </h1>
          </div>
        </div>
        <div className="w-full mt-4">
          <Button className="w-full" onClick={closeQRCodeModal}>
            Close
          </Button>
        </div>
      </Modal>
      <div className="dark:bg-dark-primary dark:text-gray-200 font-sans">
        <div className="min-h-screen flex flex-col justify-center items-center px-4">
          {data ? (
            <div>
              <div className="dark:bg-dark-secondary border dark:border-white/5 shadow-sm rounded-lg shadow-lg p-6 max-w-md w-full">
                <div className="text-center mb-6">
                  <img
                    src={data.event.logo}
                    alt={`${data.event.name} logo`}
                    className="w-24 h-24 mx-auto rounded-full mb-4"
                  />
                  <h1 className="text-xl font-semibold">{data.event.name}</h1>
                  <p className="text-muted-foreground text-orange-500 text-sm">
                    {formatDateWithoutOrdinal(data.event.startDate)}
                  </p>
                  <p className="text-xs text-gray-400">{data.event.location}</p>
                </div>

                <div className="mb-4">
                  <h1>Please see your tickets below</h1>
                </div>

                <div className="-mb-2">
                  {data.tickets.map((ticket, index) => (
                    <Card
                      key={index}
                      className="flex p-3 items-start mb-3 dark:bg-gradient-to-br dark:from-zinc-800 to-zinc-900 border dark:border-zinc-700 shadow-md rounded-lg"
                    >
                      <div className="dark:bg-white shadow-lg border dark:border-white/10 p-1 rounded-md z-10 mr-3">
                        <TicketIcon className="h-6 w-6 text-zinc-800" />
                      </div>
                      <div>
                        <h2 className="dark:text-white font-medium text-2xl">
                          {ticket.ticket}
                        </h2>
                        <div className="flex items-center justify-between mt-2">
                          <Button
                            size="sm"
                            className="mr-3"
                            onClick={() => {
                              downloadTicket.mutate(ticket.uuid);
                            }}
                          >
                            <DownloadIcon className="w-4 h-4 mr-1" />
                            Download
                          </Button>

                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setIsQRCodeModalOpen(true);
                            }}
                          >
                            <QrCode className="w-4 h-4 mr-1" />
                            View QR Code
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="max-w-md pl-3 pt-4">
                <div className="flex">
                  <div className="mr-2 flex items-center justify-center">
                    <svg
                      id="Livello_2"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 150.55 209.09"
                      className={`w-7 ${
                        isDarkMode ? "fill-white" : "fill-black"
                      }`}
                    >
                      <path d="m110.26,44.92V0h-49.61v47.45H0l19.53,24.22L0,95.89h60.64v48.56c0,21.42,5.62,37.73,16.7,48.49,11.05,10.72,27.99,16.16,50.35,16.16h22.85v-44.8h-22.85c-12.06,0-17.44-5.38-17.44-17.44v-50.97h40.29v-48.44h-40.29v-2.53Z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex items-center justify-center">
                    <h3 className="text-sm">
                      Powered by{" "}
                      <span
                        onClick={() =>
                          window.open("https://ticketr.events", "_blank")
                        }
                        className="cursor-pointer underline font-semibold text-gray-300"
                      >
                        Ticketr
                        <SquareArrowOutUpRight className="w-3 h-3 inline-block ml-1 -mt-0.5" />
                      </span>
                      . By claiming your ticket, you agree to our{" "}
                      <a href="#" className="underline">
                        Terms
                      </a>{" "}
                      and{" "}
                      <a href="#" className="underline">
                        Privacy Policy
                      </a>
                      .
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <Loading colour="white" />
            </>
          )}
        </div>
      </div>
    </AccreditationPortalLayout>
  );
};
